import { Ref, UnwrapRef, computed, ref, watch } from 'vue';
import { useRoute, useRouter, LocationQueryValue, LocationQueryRaw } from 'vue-router';
import QueryString from 'qs';
import { createDebounce } from '../utils';

export class Type {
  static isNumber = (s: string | number): number => Number(s);

  static isString = (s: string): string => s;

  static isQueryString = <T>(s: string): T | null | undefined => {
    if (s === 'undefined') {
      return undefined;
    } else if (s === 'null') {
      return null;
    }
    return QueryString.parse(s) as T;
  };
}

const convertBack = (newValue: unknown): string | null | undefined => {
  if (newValue !== null && typeof newValue === 'object') {
    return QueryString.stringify(newValue);
  }

  if (newValue === 'undefined' || newValue === undefined) {
    return undefined;
  }

  if (newValue === 'null' || newValue === null) {
    return null;
  }

  return newValue + '';
};

const debounce = createDebounce(0);

const urlParams = new URLSearchParams(window.location.search);
const initial = [...urlParams.keys()].reduce((acc, key) => {
  return {
    ...acc,
    [key]: urlParams.get(key),
  };
}, {} as LocationQueryRaw);

const globalQuery = ref<LocationQueryRaw>(initial);

/**
 * allows two way binding between browser URL and model.
 * Accepts a default value to use when browser URL query params are not set.
 * Possible to use globally since browser URL is accessible.
 */
export const useRouteQuery = <T>(
  name: string,
  valueType: (s: string) => T,
  defaultValue: T,
): { param: Ref<UnwrapRef<T>>; setValue: (v: T) => void } => {
  const route = useRoute();
  const router = useRouter();

  const query = computed(() => {
    return route.query[name];
  });

  const param: Ref<UnwrapRef<T>> = ref<T>(defaultValue);

  const setQueryParam = (newValue: T) => {
    const value = convertBack(newValue);
    if (value !== query.value) {
      globalQuery.value = { ...(globalQuery.value ?? {}), [name]: value };
    }
  };

  const setParam = (newValue: T) => {
    if (JSON.stringify(newValue) !== JSON.stringify(param.value)) {
      setValue(newValue);
    }
  };

  const setValue = (val: T) => {
    param.value = val as UnwrapRef<T>;
  };

  watch(query, (newParam: LocationQueryValue | LocationQueryValue[]) => {
    if (newParam === null) {
      if (defaultValue !== null) {
        setParam(defaultValue);
      }
      return;
    }

    if (!Array.isArray(newParam)) {
      const nqp = valueType(query.value as string);
      setParam(nqp);
    } else {
      console.warn('array query param is not supported');
    }
  });

  watch(param, (newRef) => {
    setQueryParam(newRef as T);
  });

  if (query.value) {
    const nqp = valueType(query.value as string);
    setParam(nqp);
  } else if (defaultValue) {
    setQueryParam(defaultValue);
  }

  watch(globalQuery, (newParam) => {
    debounce(() => {
      if (JSON.stringify(newParam) !== JSON.stringify(route?.query)) {
        router.push({
          path: route.path,
          hash: route.hash,
          query: globalQuery.value,
        });
      }
    });
  });

  return {
    param,
    setValue: setQueryParam,
  };
};
